/**
 * The user-facing words for an agent-proposed MetaInfo declaration.
 *
 * Kept out of the dialog component so the copy can be read and tested on its
 * own: the whole point of the approval prompt is that someone who does not
 * know what `loose_fk` means can still tell what they are agreeing to, and
 * that only holds if each kind gets one sentence naming the real objects.
 */

import type { McpMetaInfoDeclareApprovalRequest } from '../../../types/mcpUiControl';

export const DECLARE_KIND_LABELS: Record<
  McpMetaInfoDeclareApprovalRequest['kind'],
  string
> = {
  enum: 'Enum column',
  json: 'JSON column',
  loose_fk: 'Loose foreign key',
  reference_table: 'Reference table',
};

export function describeDeclaration(
  request: McpMetaInfoDeclareApprovalRequest,
): string {
  if (request.kind === 'enum') {
    // No value count here on purpose: the agent has not read the column yet.
    // Sampling it is a data read, and that only happens once you approve.
    return `Treat ${request.target} as a list of set values, so filtering it offers a dropdown instead of free text. Its values will be sampled (bounded) after you approve.`;
  }
  if (request.kind === 'json') {
    // No path structure here on purpose: the tool saves the declaration only.
    // Paths accrue as you browse the column, or when you scan it from the
    // MetaInfo manager.
    return `Treat ${request.target} as a JSON column, so filtering it offers JSON paths instead of plain text and its cells get the JSON viewer. Its path structure is not read — that happens when you browse the column or scan it yourself.`;
  }
  if (request.kind === 'loose_fk') {
    return `Record that ${request.target} points at ${request.reference}, so Jam can follow it like a foreign key your database does not declare.`;
  }
  const labels =
    request.mode === 'cached'
      ? ' Its names are read once and stored.'
      : ' Names are looked up as you scroll and nothing is stored.';
  const label = request.labelColumn
    ? ` It will use ${request.labelColumn} as the name column.`
    : '';
  return `Treat ${request.target} as a reference table, so every column pointing at it shows names instead of raw ids.${labels}${label}`;
}

/**
 * What approving costs — the two facts that decide whether saying yes is safe.
 *
 * The second one is deliberately narrower than it used to be. It said the
 * badge stays "until you edit it yourself", which is a promise the app cannot
 * keep for every kind: a loose FK and a label column have an editor, but an
 * enum declaration and a reference-table flag have no edit affordance at all —
 * the way back for those is to remove and re-declare. So this names what is
 * actually reachable from every kind: edit or re-save it if you can, remove it
 * otherwise.
 */
export function describeConsequence(
  request: McpMetaInfoDeclareApprovalRequest,
): string {
  const badge = request.provenance === 'ai-updated' ? 'AI-updated' : 'AI-created';
  return (
    'Nothing in your database changes — this is saved with your local settings. ' +
    `It will be marked ${badge} in the MetaInfo manager: editing or re-saving it yourself clears that, and removing it withdraws it.`
  );
}
